import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { AppointmentsService } from '../service/appointments.service';

interface Appointment {
  appointment_id: string;
  client: any;
  lawyer: any;
  account_information?: any;
  appointment_time: string;
  appointment_from: string;
  appointment_status: string;
  payment_status: string;
  amount_paid: any;
  payment_breakdown: any;
  settlement_status: string;
  settlement_amount: any;
  settlement_due_at: string | null;
  settlement_utr?: string;
  settlement_notes?: string;
  settlement_date?: string;
  refund?: any;
  notes?: any[];
}

interface Option {
  value: string;
  label: string;
}

@Component({
    selector: 'app-appointments',
    templateUrl: './appointments.component.html',
    styleUrls: ['./appointments.component.css'],
    standalone: false
})
export class AppointmentsComponent implements OnInit, OnDestroy {

  // ═══════════════════════════════════════════════════════════
  //  State
  //
  //  Filtering, sorting and paging all happen on the server: the table, the
  //  summary cards and the dropdown options are three reads of one filter,
  //  so a card can never disagree with the rows under it. Nothing here
  //  filters a loaded page in memory.
  // ═══════════════════════════════════════════════════════════

  rows: Appointment[] = [];
  summary: any = null;
  currency: string = 'INR';

  // ─── The settlement card ──────────────────────────────────────────────────
  //
  // The card headlines one slice of the settlement at a time. The summary
  // already carries all three amounts, so switching between them is a local
  // toggle - it costs no request.
  settlementSlices: Option[] = [
    { value: 'completed', label: 'Success' },
    { value: 'pending', label: 'Pending' },
    { value: 'failed', label: 'Failed' }
  ];

  settlementSlice: string = 'completed';

  loading: boolean = false;
  summaryLoading: boolean = false;

  pagination: any = {
    page: 1,
    limit: 10,
    total: 0,
    total_pages: 0,
    showing: { from: 0, to: 0 }
  };

  sortBy: string = 'appointment_from';
  sortOrder: 'asc' | 'desc' = 'desc';

  // ─── The date control ─────────────────────────────────────────────────────
  //
  // Every period resolves to the same two values before anything is sent:
  // `startDate` and `endDate`, as IST calendar days. `datePreset` is only
  // which chip is lit - the API never sees it, so there is no period
  // vocabulary shared across the wire to fall out of step. The UI label
  // (e.g. "Q3 2026") and the wire values are always computed separately -
  // see `dateRangeLabel` vs `getPeriodRange`.
  //
  // All six predefined periods share ONE interaction: Previous / Next
  // arrows that step `referenceDate` by a whole period at a time.
  // `referenceDate` is the only thing Previous/Next ever touch - it is not
  // re-derived from "today" on every click, so repeated navigation keeps
  // moving the same direction instead of snapping back.
  datePreset: string = 'daily';
  startDate: string = '';
  endDate: string = '';

  referenceDate: string = '';

  // Custom Selection's drafts: the picker only takes effect on Apply, like
  // the mock's date popover.
  draftStart: string = '';
  draftEnd: string = '';

  showDateMenu: boolean = false;

  readonly datePresets: { value: string; label: string }[] = [
    { value: 'daily',       label: 'Daily' },
    { value: 'weekly',      label: 'Weekly' },
    { value: 'monthly',     label: 'Monthly' },
    { value: 'quarterly',   label: 'Quarterly' },
    { value: 'half_yearly', label: 'Half yearly' },
    { value: 'yearly',      label: 'Yearly' },
    { value: 'custom',      label: 'Custom Selection' }
  ];

  // ─── The filter bar ───────────────────────────────────────────────────────
  //
  // `applied` is what the server is answering; `draft` is what the user is
  // building. Clear and Apply move between the two.
  applied = {
    appointment_status: [] as string[],
    payment_status: [] as string[],
    settlement_status: [] as string[],
    lawyer_id: [] as string[]
  };

  draft = {
    appointment_status: [] as string[],
    payment_status: [] as string[],
    settlement_status: [] as string[],
    lawyer_id: [] as string[]
  };

  openDropdown: string | null = null;

  searchQuery: string = '';

  // ─── Dropdown options, served by the filters endpoint ─────────────────────
  options: any = {
    appointment_status: [] as Option[],
    payment_status: [] as Option[],
    settlement_status: [] as Option[],
    lawyer: [] as Option[]
  };

  // ─── Row menus and modals ─────────────────────────────────────────────────
  activeMenuUid: string | null = null;
  selectedAccountId: string | null = null;

  showNotesModal: boolean = false;
  selectedAppointment: any = null;
  newNote: string = '';
  savingNote: boolean = false;

  showSettlementModal: boolean = false;
  selectedSettlementAppointment: any = null;
  utrNumber: string = '';
  settlementNotes: string = '';
  savingSettlement: boolean = false;

  showSettlementDetailsModal: boolean = false;
  selectedSettlement: any = null;

  showBankDetailsModal: boolean = false;
  selectedBank: any = null;

  private readonly search$ = new Subject<string>();
  private readonly subscriptions = new Subscription();

  // Only these two settlement states can be completed by hand: both are
  // stored as `initiated`, which is what the settlement endpoint requires.
  private readonly settleableStatuses = ['processing', 'failed'];

  constructor(
    private appointmentsService: AppointmentsService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    // Typing must not put a request on the wire per keystroke.
    this.subscriptions.add(
      this.search$
        .pipe(debounceTime(400), distinctUntilChanged())
        .subscribe(() => {
          this.pagination.page = 1;
          this.reload();
        })
    );

    // Resolve the default period into real dates before the first read.
    this.applyPeriod(this.datePreset);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  // Any outside click closes whichever popover is open.
  @HostListener('document:click')
  closeMenus(): void {
    this.showDateMenu = false;
    this.openDropdown = null;
    this.activeMenuUid = null;
    this.selectedAccountId = null;
  }

  // ═══════════════════════════════════════════════════════════
  //  API
  // ═══════════════════════════════════════════════════════════

  /**
   * The one filter every call reads. `page`, `limit` and the sort are the
   * table's alone; the summary and the dropdowns ignore them.
   */
  private params(includePaging: boolean = true): any {
    const params: any = {
      start_date: this.startDate,
      end_date: this.endDate,
      search: this.searchQuery.trim(),
      ...this.applied
    };

    if (includePaging) {
      params.page = this.pagination.page;
      params.limit = this.pagination.limit;
      params.sort_by = this.sortBy;
      params.sort_order = this.sortOrder;
    }

    return params;
  }

  /** The table and the cards, fetched together so they land together. */
  reload(): void {
    this.loadAppointments();
    this.loadSummary();
  }

  loadAppointments(): void {
    this.loading = true;
    this.spinner.show();

    this.appointmentsService.getAppointments(this.params()).subscribe({
      next: (resp: any) => {
        const data = resp?.['data'] || {};

        this.rows = data['appointments'] || [];
        this.pagination = data['pagination'] || this.pagination;
        this.currency = data['currency'] || this.currency;

        this.loading = false;
        this.spinner.hide();
      },
      error: (err: any) => {
        this.loading = false;
        this.spinner.hide();
        this.toastr.error(
          err?.error?.msg || 'Unable to load appointments.'
        );
      }
    });
  }

  loadSummary(): void {
    this.summaryLoading = true;

    this.appointmentsService.getSummary(this.params(false)).subscribe({
      next: (resp: any) => {
        this.summary = resp?.['data'] || null;
        this.summaryLoading = false;
      },
      error: () => {
        this.summaryLoading = false;
      }
    });
  }

  /**
   * The dropdown contents.
   *
   * Only the date range narrows them - not the other dropdowns - so picking
   * one lawyer cannot empty the lawyer list. That is also why this is not
   * refetched on every Apply: only a date change can change the options.
   */
  loadFilterOptions(): void {
    this.appointmentsService.getFilterOptions({
      start_date: this.startDate,
      end_date: this.endDate
    }).subscribe({
      next: (resp: any) => {
        const data = resp?.['data'] || {};

        Object.keys(this.options).forEach(key => {
          this.options[key] = data[key] || [];
        });
      },
      error: () => {
        // The screen still works with empty dropdowns; no need to shout.
      }
    });
  }

  // ═══════════════════════════════════════════════════════════
  //  The settlement card
  // ═══════════════════════════════════════════════════════════

  /** The headline amount: whichever slice is selected. */
  get settlementAmount(): any {
    return this.summary?.['settlement']?.[this.settlementSlice + '_amount'];
  }

  /** How many settlements that slice covers. */
  settlementCount(slice: string): number {
    return this.summary?.['settlement']?.[slice] || 0;
  }

  selectSettlementSlice(slice: string): void {
    this.settlementSlice = slice;
  }

  // ═══════════════════════════════════════════════════════════
  //  The date control
  // ═══════════════════════════════════════════════════════════

  /**
   * What the chip shows. This is purely a display string - it is never sent
   * to the API - so it can read "Q3 Jul - Sep 2026" while the wire still
   * gets `2026-07-01` / `2026-09-30`.
   */
  get dateRangeLabel(): string {
    if (!this.startDate || !this.endDate) {
      return 'Select dates';
    }

    switch (this.datePreset) {
      case 'daily':
        return this.formatDate(this.startDate);

      case 'monthly':
        return this.formatMonthYear(this.startDate);

      case 'quarterly':
        return `Q${this.quarterOf(this.startDate)} ${this.formatMonthShort(this.startDate)} - ${this.formatMonthShort(this.endDate)} ${this.startDate.slice(0, 4)}`;

      case 'half_yearly':
        return `${this.formatMonthYear(this.startDate)} - ${this.formatMonthYear(this.endDate)}`;

      case 'yearly':
        return this.startDate.slice(0, 4);

      default: {
        // Weekly and Custom Selection: show the resolved range.
        const from = this.formatDate(this.startDate);
        const to = this.formatDate(this.endDate);

        return from === to ? from : `${from} - ${to}`;
      }
    }
  }

  /**
   * Today in IST as `YYYY-MM-DD`.
   *
   * The API reads a bare date as an IST calendar day, so "today" has to be
   * worked out in IST too - otherwise a browser in another timezone would
   * ask for the wrong one either side of midnight.
   */
  private istDay(): string {
    return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  }

  private pad2(value: number): string {
    return value < 10 ? `0${value}` : `${value}`;
  }

  /** A local calendar date, formatted the same way as `istDay` - no UTC shift. */
  private toApiDate(date: Date): string {
    return `${date.getFullYear()}-${this.pad2(date.getMonth() + 1)}-${this.pad2(date.getDate())}`;
  }

  private quarterOf(isoDate: string): number {
    const month = Number(isoDate.slice(5, 7));

    return Math.floor((month - 1) / 3) + 1;
  }

  private formatMonthYear(isoDate: string): string {
    if (!isoDate) {
      return '-';
    }

    const [year, month] = isoDate.split('-').map(Number);

    return new Date(year, month - 1, 1).toLocaleDateString('en-IN', {
      month: 'short',
      year: 'numeric'
    });
  }

  private static readonly MONTH_SHORT = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  /**
   * 3-letter month only, no year - e.g. "Jul". Used solely to build the
   * Quarterly chip label ("Q3 Jul - Sep 2026") from the same start/end
   * dates already driving the API; it doesn't touch formatMonthYear or any
   * other period's display.
   *
   * A fixed table rather than `toLocaleDateString` - the 'en-IN' locale
   * renders September as "Sept" (4 letters), which would break the
   * "Q3 Jul - Sep 2026" format.
   */
  private formatMonthShort(isoDate: string): string {
    if (!isoDate) {
      return '-';
    }

    const month = Number(isoDate.slice(5, 7));

    return AppointmentsComponent.MONTH_SHORT[month - 1];
  }

  /**
   * The first day of the period a reference date falls in - e.g. any day in
   * September resolves Monthly to 1 Sep, any day in Q3 resolves Quarterly to
   * 1 Jul. This is the one place period boundaries are decided; both the
   * range calculation and the Previous/Next navigation build on it, so the
   * two can never disagree about where a period starts.
   */
  private periodStart(period: string, referenceIso: string): Date {
    const [y, m, d] = referenceIso.split('-').map(Number);

    switch (period) {
      case 'weekly': {
        const date = new Date(y, m - 1, d);
        const isoDow = date.getDay() === 0 ? 7 : date.getDay(); // 1=Mon..7=Sun

        return new Date(y, m - 1, d - (isoDow - 1));
      }

      case 'monthly':
        return new Date(y, m - 1, 1);

      case 'quarterly':
        return new Date(y, Math.floor((m - 1) / 3) * 3, 1);

      case 'half_yearly':
        return new Date(y, m <= 6 ? 0 : 6, 1);

      case 'yearly':
        return new Date(y, 0, 1);

      case 'daily':
      default:
        return new Date(y, m - 1, d);
    }
  }

  /** The last day of a period, given the Date `periodStart` already resolved. */
  private periodEnd(period: string, start: Date): Date {
    switch (period) {
      case 'weekly':
        return new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);

      case 'monthly':
        return new Date(start.getFullYear(), start.getMonth() + 1, 0); // day 0 = last day of this month

      case 'quarterly':
        return new Date(start.getFullYear(), start.getMonth() + 3, 0);

      case 'half_yearly':
        return new Date(start.getFullYear(), start.getMonth() + 6, 0);

      case 'yearly':
        return new Date(start.getFullYear(), 11, 31);

      case 'daily':
      default:
        return start;
    }
  }

  /**
   * The single source of truth for a predefined period's boundaries, given
   * whichever reference date it is currently anchored to. Nothing here
   * touches component state - it only computes - so `applyPeriod` and
   * `navigatePeriod` both call through it instead of duplicating the
   * arithmetic.
   */
  private getPeriodRange(period: string, referenceIso: string): { start_date: string; end_date: string } {
    const start = this.periodStart(period, referenceIso);
    const end = this.periodEnd(period, start);

    return { start_date: this.toApiDate(start), end_date: this.toApiDate(end) };
  }

  /**
   * Steps the reference date one whole period forward (`direction = 1`) or
   * backward (`direction = -1`). Always starts from the period's own first
   * day, so repeated clicks step cleanly from period to period rather than
   * drifting by however many days the previous reference date happened to
   * sit into its period.
   */
  private navigateReferenceDate(period: string, referenceIso: string, direction: number): string {
    const start = this.periodStart(period, referenceIso);

    switch (period) {
      case 'weekly':
        return this.toApiDate(new Date(start.getFullYear(), start.getMonth(), start.getDate() + direction * 7));

      case 'monthly':
        return this.toApiDate(new Date(start.getFullYear(), start.getMonth() + direction, 1));

      case 'quarterly':
        return this.toApiDate(new Date(start.getFullYear(), start.getMonth() + direction * 3, 1));

      case 'half_yearly':
        return this.toApiDate(new Date(start.getFullYear(), start.getMonth() + direction * 6, 1));

      case 'yearly':
        return this.toApiDate(new Date(start.getFullYear() + direction, 0, 1));

      case 'daily':
      default:
        return this.toApiDate(new Date(start.getFullYear(), start.getMonth(), start.getDate() + direction));
    }
  }

  /** A long month name and year, for prose like "September 2026" or "July 2026 - September 2026". */
  private formatLongMonthYear(isoDate: string): string {
    const [year, month] = isoDate.split('-').map(Number);

    return new Date(year, month - 1, 1).toLocaleDateString('en-IN', {
      month: 'long',
      year: 'numeric'
    });
  }

  /** `1` for an H1 start (January), `2` for an H2 start (July). */
  private halfOf(isoDate: string): number {
    return isoDate.slice(5, 7) === '01' ? 1 : 2;
  }

  /**
   * The human-readable "explanation" line under a period's short label -
   * e.g. "July 2026 - September 2026" for a quarter, "01 Sep 2026 - 30 Sep
   * 2026" for a month. Quarterly and Half yearly read better as full month
   * names; the rest use the existing `DD MMM YYYY` day format.
   */
  private periodRangeText(period: string, startIso: string, endIso: string): string {
    if (period === 'quarterly' || period === 'half_yearly') {
      return `${this.formatLongMonthYear(startIso)} - ${this.formatLongMonthYear(endIso)}`;
    }

    return `${this.formatDate(startIso)} - ${this.formatDate(endIso)}`;
  }

  /**
   * Tooltip text for one predefined period's boundaries - shared by the
   * current-period tooltip and the Previous/Next tooltips below, so the same
   * three lines ("prefix", "short label", "full range") are never built
   * twice with different wording.
   */
  private periodTooltipText(period: string, startIso: string, endIso: string, prefix: string | null): string {
    const lines: string[] = [];

    switch (period) {
      case 'daily':
        lines.push(prefix ? `${prefix} day` : 'Current day', this.formatDate(startIso));
        break;

      case 'weekly':
        lines.push(prefix ? `${prefix} week` : 'Current week', this.periodRangeText(period, startIso, endIso));
        break;

      case 'monthly':
        if (prefix) { lines.push(`${prefix} month`); }
        lines.push(this.formatLongMonthYear(startIso), this.periodRangeText(period, startIso, endIso));
        break;

      case 'quarterly':
        if (prefix) { lines.push(`${prefix} quarter`); }
        lines.push(`Q${this.quarterOf(startIso)} ${startIso.slice(0, 4)}`, this.periodRangeText(period, startIso, endIso));
        break;

      case 'half_yearly':
        if (prefix) { lines.push(`${prefix} half year`); }
        lines.push(`H${this.halfOf(startIso)} ${startIso.slice(0, 4)}`, this.periodRangeText(period, startIso, endIso));
        break;

      case 'yearly':
        if (prefix) { lines.push(`${prefix} year`); }
        lines.push(startIso.slice(0, 4), this.periodRangeText(period, startIso, endIso));
        break;
    }

    return lines.join('\n');
  }

  /**
   * The current-period tooltip, e.g. "Q3 2026 / July 2026 - September 2026".
   * Reads `startDate`/`endDate` directly - the same values already sent to
   * the API - so the tooltip can never describe a different period than the
   * one actually loaded.
   */
  get currentPeriodTooltip(): string {
    if (this.datePreset === 'custom' || !this.startDate || !this.endDate) {
      return '';
    }

    return this.periodTooltipText(this.datePreset, this.startDate, this.endDate, null);
  }

  /**
   * Tooltip for one arrow: the period it will navigate TO, computed with the
   * exact same `navigateReferenceDate` / `getPeriodRange` that the click
   * handler uses - so the tooltip and the click can never disagree, and it
   * updates on its own as `referenceDate` moves.
   */
  private adjacentPeriodTooltip(direction: number): string {
    if (this.datePreset === 'custom' || !this.referenceDate) {
      return '';
    }

    const targetReference = this.navigateReferenceDate(this.datePreset, this.referenceDate, direction);
    const range = this.getPeriodRange(this.datePreset, targetReference);
    const prefix = direction < 0 ? 'Previous' : 'Next';

    return this.periodTooltipText(this.datePreset, range.start_date, range.end_date, prefix);
  }

  get previousPeriodTooltip(): string {
    return this.adjacentPeriodTooltip(-1);
  }

  get nextPeriodTooltip(): string {
    return this.adjacentPeriodTooltip(1);
  }

  /** Single-line versions of the tooltips above, for `aria-label`. */
  get currentPeriodAriaLabel(): string {
    return this.currentPeriodTooltip.replace(/\n/g, ', ');
  }

  get previousPeriodAriaLabel(): string {
    return this.previousPeriodTooltip.replace(/\n/g, ', ');
  }

  get nextPeriodAriaLabel(): string {
    return this.nextPeriodTooltip.replace(/\n/g, ', ');
  }

  /** Switching the period dropdown. Custom Selection waits for its own Apply. */
  applyPeriod(period: string): void {
    this.datePreset = period;

    if (period === 'custom') {
      this.draftStart = this.startDate;
      this.draftEnd = this.endDate;
      this.showDateMenu = true;
      return;
    }

    this.showDateMenu = false;

    // Switching into a predefined period always opens on today's instance
    // of it; Previous/Next then move from there without re-touching "today".
    this.referenceDate = this.istDay();

    const range = this.getPeriodRange(period, this.referenceDate);
    this.startDate = range.start_date;
    this.endDate = range.end_date;

    this.pagination.page = 1;
    this.loadFilterOptions();
    this.reload();
  }

  /** Previous/Next for all six predefined periods. Custom Selection has none. */
  navigatePeriod(direction: number): void {
    this.referenceDate = this.navigateReferenceDate(this.datePreset, this.referenceDate, direction);

    const range = this.getPeriodRange(this.datePreset, this.referenceDate);
    this.startDate = range.start_date;
    this.endDate = range.end_date;

    this.pagination.page = 1;
    this.loadFilterOptions();
    this.reload();
  }

  goToPreviousPeriod(): void {
    this.navigatePeriod(-1);
  }

  goToNextPeriod(): void {
    this.navigatePeriod(1);
  }

  toggleDateMenu(event: Event): void {
    event.stopPropagation();

    this.showDateMenu = !this.showDateMenu;
    this.openDropdown = null;

    if (this.showDateMenu) {
      this.draftStart = this.startDate;
      this.draftEnd = this.endDate;
    }
  }

  applyDateRange(): void {
    if (this.draftStart && this.draftEnd && this.draftStart > this.draftEnd) {
      this.toastr.warning('The start date cannot be after the end date.');
      return;
    }

    this.startDate = this.draftStart;
    this.endDate = this.draftEnd;
    this.datePreset = 'custom';

    this.showDateMenu = false;
    this.pagination.page = 1;

    this.loadFilterOptions();
    this.reload();
  }

  clearDateRange(): void {
    this.showDateMenu = false;

    // Clear always falls back to the same sensible default as first load.
    this.applyPeriod('daily');
  }

  // ═══════════════════════════════════════════════════════════
  //  The filter bar
  // ═══════════════════════════════════════════════════════════

  toggleDropdown(name: string, event: Event): void {
    event.stopPropagation();

    this.openDropdown = this.openDropdown === name ? null : name;
    this.showDateMenu = false;
  }

  isChecked(field: string, value: string): boolean {
    return (this.draft as any)[field].indexOf(value) > -1;
  }

  toggleOption(field: string, value: string): void {
    const selected: string[] = (this.draft as any)[field];
    const index = selected.indexOf(value);

    if (index > -1) {
      selected.splice(index, 1);
    } else {
      selected.push(value);
    }
  }

  /** The label a closed dropdown shows: the placeholder, or what is picked. */
  dropdownLabel(field: string, placeholder: string): string {
    const selected: string[] = (this.applied as any)[field];

    if (!selected.length) {
      return placeholder;
    }

    if (selected.length === 1) {
      return this.optionLabel(field, selected[0]);
    }

    return `${placeholder} (${selected.length})`;
  }

  private optionLabel(field: string, value: string): string {
    const key = field === 'lawyer_id' ? 'lawyer' : field;

    const option = (this.options[key] || []).find(
      (item: Option) => item.value === value
    );

    return option ? option.label : this.humanise(value);
  }

  get activeFilterCount(): number {
    return Object.keys(this.applied).reduce(
      (count, key) => count + (this.applied as any)[key].length,
      0
    );
  }

  applyFilters(): void {
    Object.keys(this.draft).forEach(key => {
      (this.applied as any)[key] = [...(this.draft as any)[key]];
    });

    this.openDropdown = null;
    this.pagination.page = 1;

    this.reload();
  }

  clearFilters(): void {
    Object.keys(this.draft).forEach(key => {
      (this.draft as any)[key] = [];
      (this.applied as any)[key] = [];
    });

    this.searchQuery = '';
    this.openDropdown = null;
    this.pagination.page = 1;

    this.reload();
  }

  onSearch(): void {
    this.search$.next(this.searchQuery);
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.pagination.page = 1;
    this.reload();
  }

  // ═══════════════════════════════════════════════════════════
  //  Sorting and paging
  // ═══════════════════════════════════════════════════════════

  sortColumn(field: string): void {
    if (this.sortBy === field) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = field;
      this.sortOrder = 'desc';
    }

    this.pagination.page = 1;
    this.loadAppointments();
  }

  getSortIcon(field: string): string {
    if (this.sortBy !== field) {
      return 'bi-arrow-down-up';
    }

    return this.sortOrder === 'asc' ? 'bi-sort-down-alt' : 'bi-sort-down';
  }

  getTotalPages(): number {
    return this.pagination?.total_pages || 0;
  }

  getPageNumbers(): number[] {
    const total = this.getTotalPages();
    const current = this.pagination.page;

    if (total <= 1) {
      return total === 1 ? [1] : [];
    }

    // A five-page window that stays inside the available range.
    let start = Math.max(1, current - 2);
    const end = Math.min(total, start + 4);

    start = Math.max(1, end - 4);

    const pages: number[] = [];

    for (let page = start; page <= end; page++) {
      pages.push(page);
    }

    return pages;
  }

  goToPage(page: number): void {
    if (
      page < 1 ||
      page > this.getTotalPages() ||
      page === this.pagination.page
    ) {
      return;
    }

    this.pagination.page = page;
    this.loadAppointments();
  }

  goToPrevPage(): void {
    this.goToPage(this.pagination.page - 1);
  }

  goToNextPage(): void {
    this.goToPage(this.pagination.page + 1);
  }

  changePageSize(limit: any): void {
    this.pagination.limit = Number(limit);
    this.pagination.page = 1;
    this.loadAppointments();
  }

  getPaginationRange(): string {
    const total = this.pagination?.total || 0;

    if (!total) {
      return 'No appointments';
    }

    const showing = this.pagination.showing || {};

    return `Showing ${showing['from'] || 0} to ${showing['to'] || 0} of ${total} appointments`;
  }

  trackByAppointment(_: number, row: Appointment): string {
    return row.appointment_id;
  }

  // ═══════════════════════════════════════════════════════════
  //  Row menus
  // ═══════════════════════════════════════════════════════════

  toggleActionMenu(id: string, event: Event): void {
    event.stopPropagation();

    this.activeMenuUid = this.activeMenuUid === id ? null : id;
    this.selectedAccountId = null;
  }

  openAccountCard(appointment: Appointment, event: MouseEvent): void {
    event.stopPropagation();

    this.selectedAccountId =
      this.selectedAccountId === appointment.appointment_id
        ? null
        : appointment.appointment_id;
  }

  closeAccountCard(): void {
    this.selectedAccountId = null;
  }

  hasBankDetails(appointment: Appointment): boolean {
    return !!appointment.account_information?.account_number?.trim();
  }

  canSettle(appointment: Appointment): boolean {
    return this.settleableStatuses.indexOf(appointment.settlement_status) > -1;
  }

  // ═══════════════════════════════════════════════════════════
  //  Notes
  // ═══════════════════════════════════════════════════════════

  openNotesModal(appointment: Appointment): void {
    this.selectedAppointment = { ...appointment };
    this.newNote = '';
    this.showNotesModal = true;
  }

  closeNotesModal(): void {
    this.showNotesModal = false;
    this.selectedAppointment = null;
    this.newNote = '';
  }

  saveNote(): void {
    const note = this.newNote.trim();

    if (!note || this.savingNote) {
      return;
    }

    this.savingNote = true;
    this.spinner.show();

    this.appointmentsService
      .addNote(this.selectedAppointment.appointment_id, { note })
      .subscribe({
        next: () => {
          this.savingNote = false;
          this.spinner.hide();
          this.toastr.success('Note saved successfully.');
          this.closeNotesModal();
          this.loadAppointments();
        },
        error: (err: any) => {
          this.savingNote = false;
          this.spinner.hide();
          this.toastr.error(err?.error?.msg || 'Unable to save note.');
        }
      });
  }

  // ═══════════════════════════════════════════════════════════
  //  Settlement
  // ═══════════════════════════════════════════════════════════

  openSettlementModal(appointment: Appointment): void {
    this.selectedSettlementAppointment = appointment;
    this.utrNumber = '';
    this.settlementNotes = '';
    this.showSettlementModal = true;
  }

  closeSettlementModal(): void {
    this.showSettlementModal = false;
    this.selectedSettlementAppointment = null;
    this.utrNumber = '';
    this.settlementNotes = '';
  }

  saveSettlement(): void {
    const utr = this.utrNumber.trim();

    if (!utr || this.savingSettlement) {
      return;
    }

    this.savingSettlement = true;
    this.spinner.show();

    this.appointmentsService
      .updateSettlement(this.selectedSettlementAppointment.appointment_id, {
        settlement_utr: utr,
        settlement_notes: this.settlementNotes
      })
      .subscribe({
        next: () => {
          this.savingSettlement = false;
          this.spinner.hide();
          this.toastr.success('Settlement status updated successfully.');
          this.closeSettlementModal();

          // The settlement cards move with the row, so reload both.
          this.reload();
        },
        error: (err: any) => {
          this.savingSettlement = false;
          this.spinner.hide();
          this.toastr.error(
            err?.error?.msg || 'Unable to update settlement status.'
          );
        }
      });
  }

  openSettlementDetails(appointment: any): void {
    this.selectedSettlement = appointment;
    this.showSettlementDetailsModal = true;
  }

  closeSettlementDetails(): void {
    this.showSettlementDetailsModal = false;
    this.selectedSettlement = null;
  }

  openBankDetails(appointment: any): void {
    this.selectedBank = appointment;
    this.showBankDetailsModal = true;
  }

  closeBankDetails(): void {
    this.showBankDetailsModal = false;
    this.selectedBank = null;
  }

  // ═══════════════════════════════════════════════════════════
  //  Display helpers
  //
  //  The API returns status values, never chip text: the labels come from
  //  the filter-options endpoint, so there is one source for both.
  // ═══════════════════════════════════════════════════════════

  private humanise(value: string): string {
    return (value || '')
      .split('_')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  private labelFrom(group: string, value: string): string {
    if (!value) {
      return '-';
    }

    const option = (this.options[group] || []).find(
      (item: Option) => item.value === value
    );

    return option ? option.label : this.humanise(value);
  }

  appointmentStatusLabel(status: string): string {
    return this.labelFrom('appointment_status', status);
  }

  paymentStatusLabel(status: string): string {
    return this.labelFrom('payment_status', status);
  }

  settlementStatusLabel(status: string): string {
    return this.labelFrom('settlement_status', status);
  }

  getStatusClass(status: string): string {
    switch ((status || '').toLowerCase()) {
      case 'completed':
        return 'status-active';
      case 'confirmed':
        return 'status-confirmed';
      case 'ongoing':
        return 'status-ongoing';
      case 'pending_payment':
      case 'cancellation_pending':
      case 'resheduled':
        return 'status-pending';
      case 'cancelled':
      case 'no_show':
        return 'status-cancelled';
      case 'abandoned':
        return 'settlement-failed';
      default:
        return 'status-default';
    }
  }

  getPaymentStatusClass(status: string): string {
    switch ((status || '').toLowerCase()) {
      case 'paid':
        return 'status-active';
      case 'pending':
        return 'status-pending';
      case 'failed':
        return 'status-cancelled';
      case 'refunded':
        return 'status-refunded';
      default:
        return 'status-default';
    }
  }

  getSettlementStatusClass(status: string): string {
    switch ((status || '').toLowerCase()) {
      case 'pending':
        return 'settlement-pending';
      case 'processing':
        return 'settlement-initiated';
      case 'completed':
        return 'settlement-completed';
      case 'failed':
        return 'settlement-failed';
      case 'refunded':
        return 'status-refunded';
      default:
        return '';
    }
  }

  money(amount: any, currency: string = ''): string {
    if (amount === null || amount === undefined || amount === '') {
      return '-';
    }

    const value = Number(amount);

    if (isNaN(value)) {
      return '-';
    }

    const code = currency || this.currency || 'INR';
    const symbol = code === 'INR' ? '₹' : `${code} `;

    return symbol + value.toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  }

  formatDate(value: string | null | undefined): string {
    if (!value) {
      return '-';
    }

    const date = new Date(value);

    if (isNaN(date.getTime())) {
      return '-';
    }

    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }

  formatDateTime(value: string | null | undefined): string {
    if (!value) {
      return '-';
    }

    const date = new Date(value);

    if (isNaN(date.getTime())) {
      return '-';
    }

    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }
}